import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './OperationPage.scss';
import {
  fetchTask,
  startWorkflow,
  updateTaskStatus,
} from '@/features/orders/ordering/services/orderWorkflowService.js';
import { getVideoTypes } from '@/features/systemManagement/videoType/videoTypeService.js';
import { flowTypesOptions } from '@/constants/dropdownOptions.js';
import LoadingIndicator from '@/components/LoadingIndicator.js';
import {
  MODAL_TYPES,
  ORDER_STATUS_TYPES,
  ROLE_TYPES,
  VIDEO_TYPES,
} from '@/constants/constants.js';
import { AiTextsForm, useAiTextsForm } from './components/forms/AiTextsForm.js';
import { handleApiRequest } from '../../components/AdminTaskAction/AdminTaskAction.js';
import FlowButtons from './components/FlowButtons.js';
import ClientInfoForm, { useClientInfoForm } from './components/forms/ClientInfoForm.js';
import OrderProgressBar from './components/orderProgressBar/OrderProgressBar.js';
import SelectVideoType from './components/selectVideoType/SelectVideoType.js';
import CreateOrderForm, {
  useCreateOrderForm,
} from './components/forms/CreateOrderForm.js';
import CreateOrderFooter from './components/footer/CreateOrderFooter.js';
import OrderModal from '../../components/OrderModal/OrderModal.js';

function OperationPage({ formik }) {
  const pageRef = useRef(null);
  const navigate = useNavigate();
  const { client_id, workFlowStatus, status_id } = useParams();

  const [barStep, setBarStep] = useState();
  const [footerStep, setFooterStep] = useState();
  const role = Number(sessionStorage.getItem('role_id'));
  const user_id = sessionStorage.getItem('userid');

  const [selectedOrder, setSelectedOrder] = useState(null);

  //页面加载变量
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //视频文案变量
  const [isAiTextsVisible, setIsAiTextsVisible] = useState(false);
  const [disabled_AiText, setDisabled_AiText] = useState(true);
  const [speechLimit, setSpeechLimit] = useState(0);
  const [farewellLimit, setFarewellLimit] = useState(0);
  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] = useState(true);
  const [useAiText, setUseAiText] = useState(true);
  const [isSpeechLengthOverflow, setIsSpeechLengthOverflow] = useState(false);
  const [isFarewellLengthOverflow, setIsFarewellLengthOverflow] =
    useState(false);

  //订单状态更新变量
  const [completeBtnVisible, setCompleteBtnVisible] = useState(false);
  const [flowBtnVisible, setFlowBtnVisible] = useState(false);

  //上传资料变量
  const [disabled_info, setDisabled_info] = useState(false);
  const [totalImagesLimit, setTotalImagesLimit] = useState(0);
  const [dynamicImageLimit, setDynamicImageLimit] = useState(0);
  const [audioPartVisible, setAudioPartVisible] = useState(true);
  const [hasCustomPrice, setHasCustomPrice] = useState(false);

  //工作流开启部分
  const [adminBtnsVisible, setAdminBtnsVisible] = useState(false);
  const [flow_type, setFlow_type] = useState(flowTypesOptions[0].value);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  //modal显示
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const [originalClientInfo, setOriginalClientInfo] = useState();
  const [allVideoTypes, setAllVideoTypes] = useState([]);

  // 从接口获取视频类型数据
  useEffect(() => {
    const loadVideoTypes = async () => {
      try {
        const types = await getVideoTypes();
        setAllVideoTypes(types);
      } catch (error) {
        console.error('Failed to load video types:', error);
      }
    };
    loadVideoTypes();
  }, []);

  // 根据视频类型对象设置限制
  const setVideoTypeLimits = useCallback(
    (videoType) => {
      let totalImagesLimit = 0;
      let dynamicImageLimit = 0;
      let audioPartVisible = false;
      let speechLimit = 0;
      let farewellLimit = 0;
      let hasCustomPrice = false;

      // 根据视频类型标题查找对应的数据对象
      const videoTypeObj = allVideoTypes.find((v) => v.title === videoType);

      if (videoTypeObj) {
        hasCustomPrice = !!videoTypeObj.has_custom_price;
        // 照片总数：total_images 可能是 "10张" 或 "15-20张"
        if (videoTypeObj.total_images) {
          const nums = String(videoTypeObj.total_images).match(/\d+/g);
          if (nums && nums.length > 0) {
            totalImagesLimit = parseInt(nums[nums.length - 1], 10);
          }
        }

        // 动态照片总数：dynamic_photos 可能是 "1张" 或 "10-15张"
        if (videoTypeObj.dynamic_photos) {
          const nums = String(videoTypeObj.dynamic_photos).match(/\d+/g);
          if (nums && nums.length > 0) {
            dynamicImageLimit = parseInt(nums[nums.length - 1], 10);
          }
        }

        // 是否需要音频
        audioPartVisible = !!videoTypeObj.voice_reproduction;

        // 解析致辞/告别字数：优先 text_word_count（范围或单值），否则用 subtitle_word_count（仅致辞）
        const parseNumberFromString = (str) => {
          if (!str) return null;
          const nums = String(str).match(/\d+/g);
          if (!nums || nums.length === 0) return null;
          return parseInt(nums[nums.length - 1], 10);
        };

        const totalTextWords = parseNumberFromString(
          videoTypeObj.text_word_count
        );
        const subtitleWords = parseNumberFromString(
          videoTypeObj.subtitle_word_count
        );
        if (totalTextWords) {
          const total = totalTextWords;
          speechLimit = Math.floor((total * 3) / 4);
          farewellLimit = total - speechLimit;
        } else if (subtitleWords) {
          // 只有字幕 -> 只有致辞
          speechLimit = subtitleWords;
          farewellLimit = 0;
        } else {
          // 回退：按视频类型使用默认值（保持原有行为）
          switch (videoType) {
            case VIDEO_TYPES.数字音容版:
            case VIDEO_TYPES.数字音容Plus:
              speechLimit = 30;
              farewellLimit = 0;
              break;
            case VIDEO_TYPES.AI念影版:
            case VIDEO_TYPES.AI念影版25年版本:
              speechLimit = 200;
              farewellLimit = 50;
              break;
            case VIDEO_TYPES.AI灵犀版:
            case VIDEO_TYPES.AI灵犀版25年版本:
              speechLimit = 360;
              farewellLimit = 180;
              break;
            case VIDEO_TYPES.AI拾忆版:
            case VIDEO_TYPES.AI拾忆版25年版本:
              speechLimit = 560;
              farewellLimit = 360;
              break;
            case VIDEO_TYPES.AI惜别版:
            case VIDEO_TYPES.AI惜别版25年版本:
              speechLimit = 700;
              farewellLimit = 540;
              break;
            case VIDEO_TYPES.AI寻梦版:
            case VIDEO_TYPES.AI寻梦版25年版本:
            case VIDEO_TYPES.电影级定制:
              totalImagesLimit = undefined;
              dynamicImageLimit = undefined;
              audioPartVisible = true;
              speechLimit = undefined;
              farewellLimit = undefined;
              break;
            default:
              break;
          }
        }
      }
      setTotalImagesLimit(totalImagesLimit);
      setDynamicImageLimit(dynamicImageLimit);
      setAudioPartVisible(audioPartVisible);
      setSpeechLimit(speechLimit);
      setFarewellLimit(farewellLimit);
      setHasCustomPrice(hasCustomPrice);
    },
    [allVideoTypes]
  );

  //初始化formik
  const formik_AiTexts = useAiTextsForm(
    client_id,
    role,
    setDisabled_AiText,
    navigate,
    setIsSubmitting,
    useAiText
  );

  const formik_ClientInfo = useClientInfoForm(
    client_id,
    user_id,
    totalImagesLimit,
    dynamicImageLimit,
    audioPartVisible,
    navigate,
    setIsSubmitting,
    originalClientInfo,
    barStep
  );

  const formik_CreateOrder = useCreateOrderForm({
    client_id,
    user_id,
    formik_ClientInfo,
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const taskData = await fetchTask(client_id);
        if (taskData.client_id !== '') {
          // 资料部分内容
          formik_ClientInfo.setValues(taskData);
          formik_CreateOrder.setValues(taskData);
          setVideoTypeLimits(taskData.videoType);
          setOriginalClientInfo(taskData);
          setSelectedOrder(taskData);
          
          // 文案部分内容
          if (
            ((taskData.speech_text !== '' && taskData.farewell_text !== '') ||
             (taskData.videoType !== VIDEO_TYPES.数字音容版 && taskData.videoType !== VIDEO_TYPES.数字音容Plus)
            ) && 
             taskData.status !== ORDER_STATUS_TYPES.未提交
            ) {
            setIsAiTextsVisible(true);
            formik_AiTexts.setValues({
              speech_text: taskData.speech_text,
              farewell_text: taskData.farewell_text,
              custom_speech_text: '',
              custom_farewell_text: '',
            });
          }

          // 是否可见可编辑
          handleAdminStatus(taskData);

          // progress bar/footer 状态
          if (
            (role === ROLE_TYPES.客服 && taskData.user_id === user_id) ||
            role === ROLE_TYPES.管理员
          ) {
            if (Number(status_id) === ORDER_STATUS_TYPES.未提交) {
              setVideoTypeLimits(formik_CreateOrder.values.videoType);
              setBarStep(3);
              setFooterStep(3);
            } else if (Number(status_id) === ORDER_STATUS_TYPES.文案确认中) {
              setBarStep(4);
              setFooterStep(4);
            } else if (Number(status_id) === ORDER_STATUS_TYPES.资料修改中) {
              setFooterStep(5);
            } else if (Number(status_id) === ORDER_STATUS_TYPES.审核中) {
              if (role === ROLE_TYPES.管理员) {
                setFooterStep(6);
              }
            } else if (role !== ROLE_TYPES.管理员) {
              setModalType(MODAL_TYPES.无权限);
              setConfirmText('返回订单列表');
              setModalVisible(true);
            }
          } else {
            setModalType(MODAL_TYPES.无权限);
            setConfirmText('返回订单列表');
            setModalVisible(true);
          }
        } else {
          if (!barStep && role !== ROLE_TYPES.制作人员) {
            if (formik_CreateOrder.values.videoType !== '') {
              setBarStep(2);
              setFooterStep(2);
            } else {
              setBarStep(1);
              setFooterStep(1);
            }
          }
        }
      } catch (error) {
        console.error('获取任务数据失败:', error);
      } finally {
        setIsLoading(false);
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }

    function handleAdminStatus(taskData) {
      switch (role) {
        // 所有都可见可编辑
        case ROLE_TYPES.管理员:
          setDisabled_info(false);
          setDisabled_AiText(false);
          setCompleteBtnVisible(true);
          if (
            workFlowStatus === 'true' ||
            (taskData.status !== ORDER_STATUS_TYPES.未提交 &&
              taskData.status !== ORDER_STATUS_TYPES.文案确认中 &&
              taskData.status !== ORDER_STATUS_TYPES.审核中 &&
              taskData.status !== ORDER_STATUS_TYPES.资料修改中)
          ) {
            setAdminBtnsVisible(true);
          }
          if (
            taskData.videoType !== VIDEO_TYPES.数字音容版 ||
            taskData.videoType !== VIDEO_TYPES.数字音容Plus
          ) {
            setFlowBtnVisible(true);
          }
          break;
        case ROLE_TYPES.客服:
          setDisabled_AiText(true);
          setDisabled_info(true);
          switch (taskData.status) {
            case ORDER_STATUS_TYPES.未提交:
              setDisabled_info(false);
              break;
            case ORDER_STATUS_TYPES.文案确认中:
              setDisabled_AiText(false);
              setDisabled_info(false);
              // _setStep('4');
              break;
            case ORDER_STATUS_TYPES.资料修改中:
              setDisabled_info(false);
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status_id, formik_CreateOrder.client_id, client_id, barStep]);

  useEffect(() => {
    if (Object.keys(formik_ClientInfo.errors).length !== 0) {
      setIsConfirmButtonDisabled(true);
    } else {
      setIsConfirmButtonDisabled(false);
    }
  }, [formik_ClientInfo, formik_ClientInfo.errors, audioPartVisible]);

  useEffect(() => {
    const scrollTimer = setTimeout(() => {
      pageRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, [barStep]);

  //处理关闭Modal事件
  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const getStepComponent = () => {
    switch (barStep) {
      case 1:
        return <SelectVideoType formik={formik_CreateOrder} />;
      case 2:
        return (
          <>
            <CreateOrderForm formik={formik_CreateOrder} hasCustomPrice={hasCustomPrice} />
          </>
        );
      default:
        return (
          <>
            {isAiTextsVisible && (
              <AiTextsForm
                formik_AiTexts={formik_AiTexts}
                disabled_AiText={disabled_AiText}
                setConfirmText={setConfirmText}
                setModalType={setModalType}
                setModalVisible={setModalVisible}
                speechLimit={speechLimit}
                farewellLimit={farewellLimit}
                role={role}
                isConfirmButtonDisabled={isConfirmButtonDisabled}
                setUseAiText={setUseAiText}
                useAiText={useAiText}
                isSpeechLengthOverflow={isSpeechLengthOverflow}
                setIsSpeechLengthOverflow={setIsSpeechLengthOverflow}
                isFarewellLengthOverflow={isFarewellLengthOverflow}
                setIsFarewellLengthOverflow={setIsFarewellLengthOverflow}
              />
            )}
            <ClientInfoForm
              formik={formik_ClientInfo}
              disabled_info={disabled_info}
              client_id={client_id}
              setVideoTypeLimits={setVideoTypeLimits}
              totalImagesLimit={totalImagesLimit}
              dynamicImageLimit={dynamicImageLimit}
              audioPartVisible={audioPartVisible}
              hasCustomPrice={hasCustomPrice}
              barStep={barStep}
            />
          </>
        );
    }
  };

  return (
    <div
      ref={pageRef}
      className="operation-page"
      style={{ height: footerStep ? 'calc(100% - 6rem)' : '100%' }}
    >
      {!role ? (
        <LoadingIndicator containerClassName={'flex-row'} />
      ) : isLoading ||
        isSubmitting ||
        (role === ROLE_TYPES.客服 &&
          formik_ClientInfo.values.status !== ORDER_STATUS_TYPES.资料修改中 &&
          !barStep) ||
        role === ROLE_TYPES.制作人员 ? (
        <LoadingIndicator
          label={'提交中...请稍等'}
          containerClassName={'flex-row'}
        />
      ) : (
        <>
          {barStep && role === ROLE_TYPES.客服 ? (
            <>
              <OrderProgressBar barStep={barStep} />
            </>
          ) : (
            <>
              {adminBtnsVisible && (
                <FlowButtons
                  flowBtnVisible={flowBtnVisible}
                  completeBtnVisible={completeBtnVisible}
                  setModalType={setModalType}
                  setConfirmText={setConfirmText}
                  setModalVisible={setModalVisible}
                  setIsDownloadLoading={setIsDownloadLoading}
                  selectedOrder={selectedOrder}
                  isDownloadLoading={isDownloadLoading}
                  fetchData={async () => {
                    try {
                      const taskData = await fetchTask(client_id);
                      if (taskData.client_id !== '') {
                        setSelectedOrder(taskData);
                        formik_ClientInfo.setValues(taskData);
                        formik_CreateOrder.setValues(taskData);
                      }
                    } catch (error) {
                      console.error('刷新任务数据失败:', error);
                    }
                  }}
                />
              )}
            </>
          )}
          {getStepComponent()}
          {footerStep && (
            <CreateOrderFooter
              formik_CreateOrder={formik_CreateOrder}
              footerStep={footerStep}
              setFooterStep={setFooterStep}
              formik_ClientInfo={formik_ClientInfo}
              formik_AiTexts={formik_AiTexts}
              setConfirmText={setConfirmText}
              setModalType={setModalType}
              setModalVisible={setModalVisible}
              isConfirmButtonDisabled={isConfirmButtonDisabled}
              isSpeechLengthOverflow={isSpeechLengthOverflow}
              isFarewellLengthOverflow={isFarewellLengthOverflow}
              role={role}
              setBarStep={setBarStep}
              selectedOrder={selectedOrder}
            />
          )}
        </>
      )}
      <OrderModal
        visible={modalVisible}
        role={role}
        modalType={modalType}
        setModalType={setModalType}
        formik_AiTexts={formik_AiTexts}
        flow_type={flow_type}
        setFlow_type={setFlow_type}
        handleCloseModal={handleCloseModal}
        confirmText={confirmText}
        setConfirmText={setConfirmText}
        formik_ClientInfo={formik_ClientInfo}
        useAiText={useAiText}
        user_id={user_id}
        updateTaskStatus={updateTaskStatus}
        startWorkflow={startWorkflow}
        handleApiRequest={handleApiRequest}
        selectedOrder={selectedOrder}
        formik_CreateOrder={formik_CreateOrder}
      />
    </div>
  );
}
export default OperationPage;
